/**
 * Horton's face, as a component.
 *
 * Renders nothing until it is asked to: the renderer and the face package
 * (11 MB of glTF for the mesh face, 1.2 MB of atlas for a drawn one) are a
 * dynamic import inside `mountFace`, so a user who never opens the face pays
 * neither the bundle nor the download. That is also why this component owns
 * the mount rather than a module-level singleton — unmounting it genuinely
 * releases the renderer.
 *
 * `paused` is the cheaper cousin of unmounting: a face that is merely covered
 * (the card flipped to the transcript) keeps its renderer and stops drawing,
 * so flipping back costs nothing and never re-downloads the character.
 */
import { useEffect, useRef, useState } from 'react'

import type { FaceHandle } from '../presence/embed'
import type { FaceState } from '../presence/faces'
import { speechEnvelope } from '../services/compassSpeech'

interface Props {
  /** Hidden entirely when false; the renderer is torn down, not just blurred. */
  visible?: boolean
  face?: string
  state?: FaceState
  /** Stop drawing frames while true. The face keeps its renderer. */
  paused?: boolean
  className?: string
  onFailed?: (reason: string) => void
}

export default function HortonFace({
  visible = true, face, state = 'idle', paused = false, className, onFailed,
}: Props) {
  const host = useRef<HTMLDivElement | null>(null)
  const handleRef = useRef<FaceHandle | null>(null)
  // The handle arrives after an async import; whatever the props say by then
  // has to be applied to it, not whatever they said at mount.
  const stateRef = useRef<FaceState>(state)
  const pausedRef = useRef<boolean>(paused)
  const failedRef = useRef(onFailed)
  failedRef.current = onFailed
  const [failed, setFailed] = useState<string | null>(null)

  useEffect(() => {
    if (!visible || !host.current) return
    const el = host.current
    let cancelled = false
    setFailed(null)

    void (async () => {
      try {
        const { mountFace } = await import('../presence/embed')
        // `speechEnvelope` is passed rather than imported inside the engine so
        // the vendored code keeps knowing nothing about Horton (VENDORED.md).
        const h = await mountFace(el, speechEnvelope, face ? { face } : {})
        // Unmounted while the chunk was still downloading: mountFace has
        // already attached a canvas, so it has to be disposed, not dropped.
        if (cancelled) { h.dispose(); return }
        handleRef.current = h
        h.setState(stateRef.current)
        if (pausedRef.current) h.pause()
      } catch (e) {
        // A face that will not load must never take the conversation with it.
        // Chat is the product; this is an ornament on top of it.
        const reason = e instanceof Error ? e.message : String(e)
        if (!cancelled) { setFailed(reason); failedRef.current?.(reason) }
      }
    })()

    return () => {
      cancelled = true
      handleRef.current?.dispose()
      handleRef.current = null
    }
  }, [visible, face])

  useEffect(() => {
    stateRef.current = state
    handleRef.current?.setState(state)
  }, [state])

  useEffect(() => {
    pausedRef.current = paused
    const h = handleRef.current
    if (!h) return
    if (paused) h.pause()
    else h.resume()
  }, [paused])

  if (!visible) return null
  return (
    <div
      ref={host}
      className={className}
      style={{ width: '100%', height: '100%', overflow: 'hidden' }}
      // Decorative: it carries no information the transcript does not already
      // hold, and a screen-reader user should hear the reply, not the mouth.
      aria-hidden="true"
    >
      {failed && (
        <div style={{ padding: '0.75rem', fontSize: 12, opacity: 0.7 }}>
          Horton's face could not load. Everything else still works.
        </div>
      )}
    </div>
  )
}
